import useAxios from "axios-hooks";
import ImportJobForm from "./AssignmentForm";
import { ArrowUpTrayIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { useState, useEffect } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { UsersResDto } from "../../api/Api";
import AutocompleteInput from "../../components/input/AutoCompleteInput";
import { MembersTable } from "../members/Members";
import moment from "moment";
import PaginationButtons from "../../components/pagination/PaginationButtons";
import AssignmentForm from "./AssignmentForm";
import { getRemainingDays } from "../../util/helpers";

export default function Assignments(props: { classroomId?: string }) {
  const outletContext = useOutletContext() as any;
  const navigate = useNavigate();

  const [searchString, setSearchString] = useState(undefined as any);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [{ data, loading, error }, getImportJobs] = useAxios<UsersResDto>({
    url: "/assignments",
    params: {
      page,
      limit: pageSize,
      classroomIds: [props?.classroomId],
    },
  });

  useEffect(() => {
    outletContext?.setTitleComponent(
      <div className="flex items-center justify-center gap-2">
        <PencilSquareIcon className="h-8 w-8" />
        Assignments
      </div>
    );
    if (searchString !== undefined) {
      getImportJobs();
    }
  }, [searchString]);

  return (
    <div className="flex flex-col gap-2 p-5">
      <div className="flex">
        <div className="mb-2">
          <AutocompleteInput
            onComplete={(input: string) => {
              setSearchString(input);
            }}
            placeHolder="Search..."
            className="bg-base-200"
          ></AutocompleteInput>
        </div>
        <div className="ml-auto">
          <button
            className="btn btn-sm ml-4"
            onClick={() => {
              (document?.getElementById("import_job") as any)?.showModal();
            }}
          >
            <ArrowUpTrayIcon className="h-4" />
            Create new assignment
          </button>
        </div>
      </div>
      {!loading ? (
        <div className="flex gap-4 flex-col">
          <AssignmentsTable data={data?.docs}></AssignmentsTable>
          <PaginationButtons
            page={page}
            setPage={setPage}
            pageCount={data?.totalPages || 1}
            pageSize={pageSize}
            setPageSize={setPageSize}
          />
        </div>
      ) : (
        <div className="h-[20vh] w-full flex items-center justify-center">
          <span className="loading loading-spinner loading-md"></span>
        </div>
      )}

      <dialog id="import_job" className="modal ">
        <div className="modal-box">
          <h3 className="font-bold text-lg pb-2">Create Assignment</h3>
           <form method="dialog">
            <button className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2">
              ✕
            </button>
          </form>
          <AssignmentForm
            onSuccess={(data: any) => {
              navigate(`/assignments/${data?._id}/courses`);
            }}
          ></AssignmentForm>
        </div>
      </dialog>
    </div>
  );
}

export function AssignmentsTable({ data }: { data: any }) {
  const navigate = useNavigate();

  return (
    <div className="overflow-x-auto rounded-xl border border-base-300 bg-base-100 table-container">
      <table className="table table-md w-full">
        <thead className="bg-base-200 text-base-content sticky top-0 z-10">
          <tr>
            <th className="text-sm">Assignment ID</th>
            <th className="text-sm">Title</th>
            <th className="text-sm text-center">Classrooms</th>
            <th className="text-sm text-center">Students</th>
            <th className="text-sm text-center">Start Date</th>
            <th className="text-sm text-center">End Date</th>
            <th className="text-sm text-center">Status</th>
            <th className="text-sm text-center">Created</th>
          </tr>
        </thead>
        <tbody>
          {data?.map((assignment: any) => {
            const totalStudents = assignment.classrooms?.reduce(
              (sum: number, classroom: any) =>
                sum + (classroom.users?.length || 0),
              0
            );

            const startDate = moment(parseInt(assignment.startDate));
            const endDate = moment(parseInt(assignment.endDate));

            const remaningDays = getRemainingDays(endDate);
            const hasStarted = startDate.isBefore(moment());
            const hasEnded = remaningDays === 0;

            const getStatusMessage = () => {
              if (hasEnded) return "Assignment has ended";
              if (!hasStarted)
                return `Starts in ${getRemainingDays(startDate)} days`;
              return `Ends in ${getRemainingDays(endDate)} days`;
            };

            return (
              <tr
                key={assignment._id}
                className="hover:bg-base-300 transition cursor-pointer"
                onClick={() =>
                  navigate(`/assignments/${assignment._id}/courses`)
                }
              >
                <td className="max-w-[150px] text-xs text-wrap break-all text-base-content/70">
                  <div className="tooltip" data-tip={assignment._id}>
                    {assignment._id.slice(0, 6)}...{assignment._id.slice(-4)}
                  </div>
                </td>
                <td className="font-medium">{assignment.title}</td>
                <td className="text-center">{assignment.classrooms?.length}</td>
                <td className="text-center">{totalStudents}</td>
                <td className="text-center text-sm">
                  {startDate.format("DD-MM-YYYY")}
                </td>
                <td className="text-center text-sm">
                  <div>{endDate.format("DD-MM-YYYY")}</div>
                </td>
                <td className="text-center text-sm">{getStatusMessage()}</td>
                <td className="text-center text-sm opacity-70">
                  {moment(assignment.createdAt).fromNow()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
